import React, { useState } from "react";
import "@/App.css";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Zap,
  BadgeCheck,
  Monitor,
  Smartphone,
  Globe2,
  Search,
  Wrench,
  ShoppingBag,
  Building2,
  Gem,
  Home,
  Utensils,
  Shirt,
  GraduationCap,
  Stethoscope,
  LayoutTemplate,
  Server,
  Menu,
  X,
  Sparkles,
} from "lucide-react";

/* ---------------- Business (update phone / WhatsApp when available) ---------------- */
const COMPANY = "ANISUN SOLUTIONS";
const TAGLINE = "Digital Bundles for Your Business";
const PROFILE =
  "Website design and digital bundles tailored for small businesses — clear pricing, fast delivery, and ongoing support.";
const EMAIL = "anisunsolutions@gmail.com";
const PHONE = "+919014571727";
const PHONE_DISPLAY = PHONE ? PHONE.replace(/(\+\d{2})(\d{5})(\d{5})/, "$1 $2 $3") : "";
const WHATSAPP_NUMBER = "919014571727"; // digits only, country code without +
const LOCATION = "Hyderabad, Telangana, India";
const WEBSITE = "https://anisunsolutions.com";

const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent(
  "Website enquiry — ANISUN SOLUTIONS",
)}`;
const WHATSAPP_URL = WHATSAPP_NUMBER
  ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      "Hello ANISUN SOLUTIONS, I would like a website for my business. Please share details.",
    )}`
  : "";

const BASE = process.env.PUBLIC_URL || "";
const asset = (p) => `${BASE}${p.startsWith("/") ? p : `/${p}`}`;
const LOGO = asset("/anisun-logo.png");
/** Google Maps embed (Share → Embed) */
const GOOGLE_MAPS_EMBED_URL =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.104507386071!2d78.5940166!3d17.406771600000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9f0008a73cd9%3A0x7acb091d6024eab6!2sflyeuroimmigrationservices!5e0!3m2!1sen!2sin!4v1778436128052!5m2!1sen!2sin";

/** Same place in full Google Maps (coordinates from embed) */
const GOOGLE_MAPS_PLACE_URL =
  "https://www.google.com/maps/search/?api=1&query=17.4067716,78.5940166";

const NAV = [
  { href: "#services", label: "Services" },
  { href: "#bundle", label: "Bundle" },
  { href: "#industries", label: "Industries" },
  { href: "#pricing", label: "Pricing" },
  { href: "#about", label: "About" },
  { href: "#contact", label: "Contact" },
];

const MARQUEE = [
  "Responsive web design",
  "Domain & hosting setup",
  "Business email",
  "SEO-friendly pages",
  "WhatsApp click-to-chat",
  "Social profile kit",
  "Fast launch",
  "Small-business pricing",
];

const SERVICE_CARDS = [
  {
    icon: LayoutTemplate,
    title: "Website design",
    text:
      "Modern, mobile-first pages that reflect your brand — hero, services, gallery, and contact tuned for local customers.",
  },
  {
    icon: Server,
    title: "Domain & hosting",
    text:
      "We help you pick a domain, configure hosting, SSL, and backups so your site stays fast and secure.",
  },
  {
    icon: Mail,
    title: "Business email",
    text:
      "Professional addresses on your domain (e.g. info@yourbusiness.com) with setup on phone and laptop.",
  },
  {
    icon: Sparkles,
    title: "Social & extras",
    text:
      "Cover images, link-in-bio style landing, Google Business basics, and handover notes you can actually use.",
  },
];

const WHAT_YOU_GET = [
  "Custom layout aligned to your business",
  "Mobile-friendly, responsive design",
  "Contact forms & WhatsApp buttons",
  "Basic on-page SEO setup",
  "Domain + hosting guidance (1st year options)",
  "Launch checklist & post-go-live support window",
];

const CORE_SERVICES = [
  {
    icon: Monitor,
    title: "Responsive web design",
    text: "Sites that read well on phones, tablets, and desktops — performance-minded and easy to update.",
  },
  {
    icon: Globe2,
    title: "Domain & hosting",
    text: "End-to-end setup: DNS, SSL certificates, and a hosting plan suited to your traffic and budget.",
  },
  {
    icon: Search,
    title: "SEO foundations",
    text: "Clean structure, meta tags, and local signals so Google can understand what you offer and where you operate.",
  },
  {
    icon: Wrench,
    title: "Care & maintenance",
    text: "Security updates, small content tweaks, and optional retainers so your site does not go stale after launch.",
  },
];

const INDUSTRIES = [
  { icon: Stethoscope, name: "Clinics & diagnostics", hint: "Appointments, timings, maps" },
  { icon: GraduationCap, name: "Schools & training", hint: "Programs, faculty, admissions" },
  { icon: ShoppingBag, name: "Shops & retail", hint: "Catalogues, offers, directions" },
  { icon: Gem, name: "Jewellery & studios", hint: "Collections, craftsmanship story" },
  { icon: Home, name: "Real estate", hint: "Listings, locality, lead capture" },
  { icon: Utensils, name: "Restaurants & cafés", hint: "Menu, hours, reservations" },
  { icon: Shirt, name: "Boutiques & fashion", hint: "Lookbooks, seasons, WhatsApp orders" },
  { icon: Building2, name: "Professional services", hint: "Trust, team, case highlights" },
];

const WHY_US = [
  {
    icon: ShieldCheck,
    title: "Built for small businesses",
    text:
      "No jargon-heavy retainers by default — we scope around what you need now and what you can grow into later.",
  },
  {
    icon: Zap,
    title: "Launch-focused delivery",
    text:
      "Structured milestones from content collection to go-live, so you are not stuck halfway with an unfinished site.",
  },
  {
    icon: BadgeCheck,
    title: "Quality you can show",
    text:
      "Readable typography, accessible contrast, and responsive layouts that look credible to first-time visitors.",
  },
  {
    icon: Smartphone,
    title: "Mobile-first mindset",
    text:
      "Most of your customers will find you on a phone — we design for that screen first, then scale up beautifully.",
  },
];

/* ---------------- Logo (1024×225 PNG, transparent — source: scripts/jpg-to-transparent-png.mjs) ---------------- */
const BrandLogo = ({ className = "", variant = "default" }) => (
  <img
    src={LOGO}
    width={1024}
    height={225}
    alt={`${COMPANY} — ${TAGLINE}`}
    decoding="async"
    fetchPriority="high"
    className={`h-14 sm:h-16 md:h-[4.25rem] lg:h-[4.5rem] w-auto max-w-[min(100%,320px)] sm:max-w-[400px] md:max-w-[460px] lg:max-w-[520px] object-contain object-left border-0 outline-none ring-0 shadow-none block ${
      variant === "onDark" ? "drop-shadow-[0_2px_12px_rgba(0,0,0,0.45)]" : ""
    } ${className}`}
  />
);

/* ---------------- Header ---------------- */
const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-[var(--fe-border)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="h-[5.25rem] md:h-24 lg:h-[5.5rem] flex items-center justify-between gap-3">
          <a
            href="#top"
            data-testid="brand-link"
            className="group inline-flex items-center min-w-0 border-0 outline-none ring-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fe-navy)]/30 focus-visible:ring-offset-2 rounded-sm"
          >
            <BrandLogo />
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-[13px] font-semibold uppercase tracking-wide text-slate-600 hover:text-[var(--fe-navy)] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {PHONE ? (
              <a
                href={`tel:${PHONE}`}
                className="hidden md:inline-flex items-center gap-2 px-4 h-10 border border-slate-200 text-slate-800 text-[13px] font-semibold hover:border-[var(--fe-navy)] hover:text-[var(--fe-navy)] transition-colors"
              >
                <Phone className="w-4 h-4" /> Call
              </a>
            ) : null}
            {WHATSAPP_URL ? (
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="hidden sm:inline-flex items-center gap-2 px-4 h-10 bg-[#25D366] hover:bg-[#1da851] text-white text-[13px] font-bold transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            ) : null}
            <a
              href={MAILTO}
              data-testid="header-cta"
              className="inline-flex items-center gap-2 px-4 md:px-5 h-10 bg-[var(--fe-orange)] hover:bg-[var(--fe-orange-deep)] text-white text-[13px] font-bold transition-colors uppercase tracking-wide"
            >
              <Mail className="w-4 h-4" />
              <span className="hidden min-[400px]:inline">Get quote</span>
              <span className="min-[400px]:hidden">Quote</span>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center border border-slate-200 text-slate-800"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div
            data-testid="mobile-nav"
            className="lg:hidden border-t border-slate-200 py-4 grid gap-1"
          >
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-2 py-3 text-[15px] font-semibold text-slate-800 hover:bg-slate-50 uppercase tracking-wide"
              >
                {l.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

/* ---------------- Hero wave ---------------- */
const HeroWave = () => (
  <div className="absolute bottom-0 left-0 right-0 text-white leading-none pointer-events-none">
    <svg
      className="w-full h-12 sm:h-16 md:h-24 block"
      viewBox="0 0 1440 120"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        fill="#eef2f8"
        d="M0,64L60,58.7C120,53,240,43,360,42.7C480,43,600,53,720,58.7C840,64,960,64,1080,58.7C1200,53,1320,43,1380,37.3L1440,32L1440,120L1380,120C1320,120,1200,120,1080,120C960,120,840,120,720,120C600,120,480,120,360,120C240,120,120,120,60,120L0,120Z"
      />
    </svg>
  </div>
);

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[72vh] md:min-h-[78vh] flex flex-col justify-center overflow-hidden pt-[5.25rem] md:pt-24 lg:pt-[5.5rem] pb-6"
  >
    <div className="pointer-events-none absolute inset-0 fe-hero-scrim" aria-hidden />
    <div className="pointer-events-none absolute inset-0 fe-hero-dm-bg fe-hero-mesh" aria-hidden />
    <div className="pointer-events-none absolute inset-0 fe-grid-bg opacity-[0.12]" aria-hidden />
    <div
      className="pointer-events-none absolute -top-32 right-[-10%] h-[min(90vw,520px)] w-[min(90vw,520px)] rounded-full fe-hero-glow opacity-40"
      style={{
        background:
          "radial-gradient(circle, rgba(234,88,12,0.35) 0%, transparent 68%)",
      }}
      aria-hidden
    />
    <div
      className="pointer-events-none absolute bottom-0 left-[-5%] h-72 w-72 rounded-full opacity-30"
      style={{
        background:
          "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)",
      }}
      aria-hidden
    />

    <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full py-10 md:py-14">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-6 text-white fe-rise">
          <div className="inline-flex items-center gap-2 rounded-sm border border-white/20 bg-white/10 px-3 py-1.5 shadow-lg backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--fe-orange)]" />
            <span className="fe-overline text-white/90">{TAGLINE}</span>
          </div>

          <h1 className="font-display mt-4 text-3xl font-extrabold uppercase leading-[1.12] tracking-tight text-white sm:text-4xl md:text-5xl lg:text-[2.65rem] xl:text-5xl">
            One bundle.
            <br />
            <span className="text-[var(--fe-orange)]">One price.</span>
            <br />
            <span className="text-white/95">Your entire digital presence.</span>
          </h1>

          <p className="mt-4 text-[13px] font-bold uppercase tracking-[0.12em] text-orange-200 sm:text-sm">
            Website design · Domain help · Business email · Social setup
          </p>

          <p className="mt-3 max-w-xl text-base leading-relaxed text-slate-200">
            {PROFILE}
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={MAILTO}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2.5 px-6 h-14 bg-[var(--fe-orange)] hover:bg-[var(--fe-orange-deep)] text-white text-[15px] font-bold transition-colors uppercase tracking-wide shadow-lg shadow-orange-900/30"
            >
              Get started today
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            {WHATSAPP_URL ? (
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-14 items-center gap-2.5 border-2 border-white/35 bg-white/10 px-6 text-[15px] font-semibold text-white backdrop-blur-sm transition-colors hover:border-white hover:bg-white/20"
              >
                <MessageCircle className="h-5 w-5" />
                WhatsApp
              </a>
            ) : null}
          </div>
        </div>

        <div className="hidden fe-rise lg:col-span-6 lg:block" style={{ animationDelay: "0.12s" }}>
          <HeroVisual />
        </div>
      </div>
    </div>

    <HeroWave />

    <a
      href="#services"
      className="absolute bottom-8 md:bottom-10 left-1/2 z-10 -translate-x-1/2 text-[11px] uppercase tracking-[0.28em] text-slate-300 transition-colors hover:text-[var(--fe-orange)]"
      data-testid="hero-scroll"
    >
      Scroll ↓
    </a>
  </section>
);

/* ---------------- Hero visual (abstract art — no duplicate copy) ---------------- */
const HeroVisual = () => (
  <div
    className="relative mx-auto hidden aspect-[5/4] w-full max-w-[500px] lg:block"
    data-testid="hero-visual"
    aria-hidden
  >
    <div className="absolute inset-0 rounded-[2rem] border border-white/10 bg-white/[0.04] backdrop-blur-[2px]" />

    <div className="fe-drift absolute left-[6%] top-[10%] h-24 w-24 rounded-full bg-[var(--fe-orange)]/25 blur-2xl" />
    <div className="fe-drift-alt absolute bottom-[12%] right-[8%] h-32 w-32 rounded-full bg-[var(--fe-navy-mid)]/35 blur-3xl" />
    <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/10 blur-3xl" />

    {/* Desktop wireframe */}
    <div className="absolute inset-x-[10%] top-[12%] bottom-[28%] overflow-hidden rounded-xl border border-white/20 bg-gradient-to-br from-white/20 via-white/10 to-transparent shadow-2xl shadow-black/20 backdrop-blur-md">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2.5">
        <span className="h-2 w-2 rounded-full bg-[var(--fe-orange)]" />
        <span className="h-2 w-2 rounded-full bg-white/30" />
        <span className="h-2 w-2 rounded-full bg-white/30" />
        <span className="ml-2 h-2 flex-1 max-w-[40%] rounded-full bg-white/15" />
      </div>
      <div className="space-y-3 p-4">
        <div className="h-3 w-[55%] rounded-full bg-white/25" />
        <div className="h-2 w-[85%] rounded-full bg-white/12" />
        <div className="h-2 w-[70%] rounded-full bg-white/12" />
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[0, 1, 2].map((n) => (
            <div key={n} className="aspect-[4/3] rounded-md bg-white/10 ring-1 ring-white/15" />
          ))}
        </div>
        <div className="flex gap-2 pt-1">
          <div className="h-8 w-20 rounded-md bg-[var(--fe-orange)]/50" />
          <div className="h-8 w-16 rounded-md bg-white/15" />
        </div>
      </div>
    </div>

    {/* Phone wireframe */}
    <div className="fe-drift-alt absolute bottom-[14%] right-[4%] w-[26%] overflow-hidden rounded-[1.25rem] border-[3px] border-white/25 bg-white/10 p-1.5 shadow-xl backdrop-blur-md">
      <div className="rounded-[0.85rem] bg-gradient-to-b from-white/15 to-white/5 p-2">
        <div className="mx-auto mb-2 h-1 w-8 rounded-full bg-white/25" />
        <div className="space-y-1.5">
          <div className="h-1.5 w-full rounded-full bg-white/20" />
          <div className="h-1.5 w-[80%] rounded-full bg-white/12" />
          <div className="mt-2 h-8 w-full rounded-md bg-[var(--fe-orange)]/40" />
        </div>
      </div>
    </div>

    {/* Growth curve */}
    <svg
      className="absolute bottom-[8%] left-[8%] w-[42%] text-[var(--fe-orange)]"
      viewBox="0 0 160 80"
      fill="none"
      aria-hidden
    >
      <path
        d="M8 68 L40 52 L72 58 L112 28 L152 12"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.85"
      />
      <circle cx="152" cy="12" r="5" fill="currentColor" />
    </svg>

    {/* Connection nodes */}
    <svg className="absolute right-[18%] top-[22%] w-[35%] text-white/25" viewBox="0 0 120 120" aria-hidden>
      <circle cx="60" cy="20" r="4" fill="currentColor" />
      <circle cx="20" cy="70" r="4" fill="currentColor" />
      <circle cx="100" cy="85" r="4" fill="currentColor" />
      <path d="M60 20 L20 70 L100 85" stroke="currentColor" strokeWidth="1.5" opacity="0.6" />
    </svg>
  </div>
);

const KeywordMarquee = () => (
  <div
    data-testid="keyword-marquee"
    className="bg-gradient-to-r from-[var(--fe-navy)] via-[var(--fe-navy-mid)] to-[var(--fe-navy)] py-3.5 overflow-hidden border-y border-[var(--fe-navy-deep)]/20"
  >
    <div className="flex fe-marquee-track whitespace-nowrap">
      {[...MARQUEE, ...MARQUEE].map((t, i) => (
        <span
          key={i}
          className="flex items-center gap-3 px-6 text-[13px] font-medium text-white/90 uppercase tracking-wide"
        >
          <span className="w-1 h-1 bg-[var(--fe-orange)] rounded-full shrink-0" />
          {t}
        </span>
      ))}
    </div>
  </div>
);

const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="fe-section bg-white fe-stamp-bg text-[var(--fe-ink)]"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="fe-divider-accent" />
        <div className="fe-overline text-[var(--fe-navy)] mb-2">What we do</div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[var(--fe-ink)] leading-tight uppercase tracking-tight">
          Digital empowerment for{" "}
          <span className="text-[var(--fe-navy)]">small businesses</span>
        </h2>
        <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
          From first sketch to launch day, we keep the stack simple and the message clear — so
          customers understand you in seconds.
        </p>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SERVICE_CARDS.map((s, i) => (
          <article
            key={s.title}
            className="bg-white border border-[var(--fe-border)] p-7 rounded-lg shadow-sm hover:border-[var(--fe-navy)]/25 hover:shadow-md transition-all fe-rise"
            style={{ animationDelay: `${i * 0.06}s` }}
          >
            <div className="w-12 h-12 rounded-full bg-[var(--fe-orange)]/15 flex items-center justify-center text-[var(--fe-orange)]">
              <s.icon className="w-6 h-6" strokeWidth={2} />
            </div>
            <h3 className="font-display text-lg font-bold text-[var(--fe-ink)] mt-3 uppercase tracking-wide">
              {s.title}
            </h3>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const Bundle = () => (
  <section id="bundle" data-testid="bundle-section" className="fe-section bg-white text-[var(--fe-ink)]">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-start">
        <div className="lg:col-span-5">
          <span className="fe-divider-accent" />
          <div className="fe-overline text-[var(--fe-navy)] mb-2">What you get</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[var(--fe-ink)] uppercase tracking-tight leading-tight">
            Core deliverables in every project
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Exact pages vary by industry, but these are the outcomes you can expect when you work
            with {COMPANY}.
          </p>
          <ul className="mt-6 space-y-3">
            {WHAT_YOU_GET.map((line) => (
              <li key={line} className="flex items-start gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-[var(--fe-success)] shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-7">
          <div className="fe-overline text-[var(--fe-orange)] mb-2">Our core services</div>
          <div className="grid sm:grid-cols-2 gap-5">
            {CORE_SERVICES.map((c) => (
              <div
                key={c.title}
                className="p-6 border border-[var(--fe-border)] bg-[var(--fe-bg)] rounded-lg flex gap-4"
              >
                <div className="w-11 h-11 shrink-0 rounded-full bg-[var(--fe-navy)] text-white flex items-center justify-center">
                  <c.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-[var(--fe-ink)]">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Industries = () => (
  <section
    id="industries"
    data-testid="industries-section"
    className="fe-section bg-white text-[var(--fe-ink)]"
  >
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="fe-divider-accent" />
        <div className="fe-overline text-[var(--fe-navy)] mb-2">Who we serve</div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[var(--fe-ink)] uppercase tracking-tight leading-tight">
          Perfect fit for local businesses
        </h2>
        <p className="mt-4 text-slate-600 text-base md:text-lg">
          We adapt structure, imagery, and calls-to-action for your sector — so visitors see
          themselves in your story.
        </p>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {INDUSTRIES.map((row) => (
          <div
            key={row.name}
            className="group flex gap-4 p-5 bg-white border border-[var(--fe-border)] rounded-lg hover:border-[var(--fe-navy)]/30 transition-colors"
          >
            <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-[var(--fe-navy)]/10 text-[var(--fe-navy)] group-hover:bg-[var(--fe-orange)]/15 group-hover:text-[var(--fe-orange)] transition-colors">
              <row.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display font-bold text-[var(--fe-ink)] text-sm leading-snug">
                {row.name}
              </div>
              <div className="text-xs text-slate-500 mt-1">{row.hint}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="fe-section bg-gradient-to-br from-[var(--fe-navy-mid)] via-[var(--fe-navy)] to-[var(--fe-navy-deep)] text-white relative overflow-hidden"
  >
    <div className="absolute inset-0 fe-grid-bg opacity-20" />
    <div
      className="absolute -right-20 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full opacity-30 pointer-events-none"
        style={{
        background: "radial-gradient(circle, rgba(249,115,22,0.22) 0%, transparent 65%)",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-5">
          <span className="fe-divider-accent" />
          <div className="fe-overline text-[var(--fe-orange)] mb-2">Starter bundle</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-tight">
            Professional business website
          </h2>
          <p className="mt-4 text-white/80 leading-relaxed">
            A focused launch package for small shops, clinics, schools, and service businesses —
            ask for a detailed line-item quote.
          </p>
        </div>
        <div className="lg:col-span-7">
          <div className="bg-white/10 backdrop-blur border border-white/15 p-8 md:p-10 rounded-lg">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-white/70 text-sm uppercase tracking-widest">From</div>
                <div className="font-display text-5xl md:text-6xl font-extrabold text-white mt-1">
                  ₹9,999
                </div>
                <div className="text-[var(--fe-orange)] font-semibold mt-1">+ taxes · indicative</div>
              </div>
              <div className="flex-1 min-w-[200px] space-y-2 text-sm text-white/90">
                {[
                  "Increase visibility online",
                  "Turn visitors into WhatsApp leads",
                  "Look credible next to bigger brands",
                  "Launch in weeks, not months",
                ].map((t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[var(--fe-orange)] shrink-0" />
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <a
              href={MAILTO}
              className="mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 h-14 bg-[var(--fe-orange)] hover:bg-[var(--fe-orange-deep)] text-white font-bold uppercase tracking-wide text-sm transition-colors"
            >
              Request exact quote
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about-section" className="fe-section bg-white text-[var(--fe-ink)]">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-6">
          <span className="fe-divider-accent" />
          <div className="fe-overline text-[var(--fe-navy)] mb-2">About {COMPANY}</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[var(--fe-ink)] uppercase tracking-tight leading-tight">
            Website design with small business realities in mind
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We help owners move from “no website yet” to a professional presence — with messaging
            that fits how you actually sell: phone, WhatsApp, walk-ins, and referrals. Based in{" "}
            {LOCATION}, we work with teams who want practical guidance, not buzzwords.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={MAILTO}
              className="inline-flex items-center gap-2 px-6 h-12 bg-[var(--fe-navy)] hover:bg-[var(--fe-navy-deep)] text-white text-sm font-bold transition-colors uppercase tracking-wide"
            >
              <Mail className="w-4 h-4" />
              Email us
            </a>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="relative rounded-lg overflow-hidden border border-[var(--fe-border)] aspect-[4/3] bg-slate-100">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80"
              alt="Team collaborating on a project"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--fe-navy-deep)]/75 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="font-display font-bold text-lg uppercase">Build. Grow. Connect.</div>
              <p className="text-sm text-white/85 mt-1">
                Your site is the hub — we connect it to how customers already reach you.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section id="why" data-testid="why-section" className="fe-section bg-white text-[var(--fe-ink)]">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="max-w-3xl">
        <span className="fe-divider-accent" />
        <div className="fe-overline text-[var(--fe-navy)] mb-2">Why choose us</div>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[var(--fe-ink)] uppercase tracking-tight">
          Clear scope. Honest timelines.
        </h2>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-8 lg:gap-x-10">
        {WHY_US.map((w, i) => (
          <div key={w.title} className="flex gap-5">
            <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[var(--fe-navy)] text-white rounded-lg">
              <w.icon className="w-6 h-6" />
            </div>
            <div>
              <div className="fe-overline text-slate-400">0{i + 1}</div>
              <h3 className="font-display text-xl font-bold text-[var(--fe-ink)] mt-1">{w.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{w.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" data-testid="contact-section" className="fe-section bg-white text-[var(--fe-ink)]">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <span className="fe-divider-accent" />
          <div className="fe-overline text-[var(--fe-navy)] mb-2">Contact</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-[var(--fe-ink)] uppercase tracking-tight leading-tight">
            Tell us about your business
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Share your business name, city, and what you want customers to do on the website
            (call, WhatsApp, form, directions). We will reply with next steps and timeline.
          </p>

          <div className="mt-6 space-y-1">
            <ContactRow icon={Mail} label="Email" value={EMAIL} href={MAILTO} testId="contact-email" accent />
            {PHONE ? (
              <ContactRow icon={Phone} label="Phone" value={PHONE_DISPLAY} href={`tel:${PHONE}`} testId="contact-phone" />
            ) : null}
            {WHATSAPP_URL ? (
              <ContactRow
                icon={MessageCircle}
                label="WhatsApp"
                value="Message us"
                href={WHATSAPP_URL}
                external
                testId="contact-whatsapp"
              />
            ) : null}
            <ContactRow icon={MapPin} label="Location" value={LOCATION} testId="contact-location" />
            <ContactRow icon={Globe2} label="Web" value={WEBSITE.replace(/^https?:\/\//, "")} href={WEBSITE} external testId="contact-web" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={MAILTO}
              data-testid="contact-primary"
              className="inline-flex items-center gap-2 px-6 h-12 bg-[var(--fe-orange)] hover:bg-[var(--fe-orange-deep)] text-white text-sm font-bold uppercase tracking-wide transition-colors"
            >
              <Mail className="w-4 h-4" />
              Email for a quote
            </a>
            {WHATSAPP_URL ? (
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 h-12 border-2 border-[var(--fe-navy)] text-[var(--fe-navy)] text-sm font-bold hover:bg-[var(--fe-navy)] hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            ) : null}
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="relative w-full min-h-[260px] sm:min-h-[320px] md:min-h-[380px] rounded-lg border border-[var(--fe-border)] overflow-hidden bg-[var(--fe-bg)]">
            <iframe
              title={`${COMPANY} — office on Google Maps`}
              data-testid="office-map"
              src={GOOGLE_MAPS_EMBED_URL}
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <a
              href={GOOGLE_MAPS_PLACE_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="open-in-maps"
              className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-2 bg-white/95 backdrop-blur text-[var(--fe-navy)] text-xs font-bold hover:bg-white transition-colors shadow-lg border border-[var(--fe-border)]"
            >
              Open in Google Maps
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            Visit us in Hyderabad — or reach out remotely. Email and WhatsApp (when configured) are
            the fastest way to start a project brief.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const ContactRow = ({ icon: Icon, label, value, href, external, testId, accent }) => {
  const inner = (
    <>
      <div
        className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-lg ${
          accent ? "bg-[var(--fe-orange)] text-white" : "bg-[var(--fe-navy)] text-white"
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="fe-overline text-slate-400">{label}</div>
        <div className="text-[15px] font-semibold text-[var(--fe-ink)] mt-0.5 break-words">{value}</div>
      </div>
      {href ? (
        <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-[var(--fe-navy)] transition-colors shrink-0" />
      ) : null}
    </>
  );
  const cls =
    "group flex items-center gap-4 py-4 border-b border-[var(--fe-border)] last:border-b-0 transition-colors rounded-sm";
  if (href) {
    return (
      <a
        href={href}
        data-testid={testId}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className={cls}
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testId} className={cls}>
      {inner}
    </div>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[var(--fe-footer)] text-slate-300 pt-10 pb-6 border-t border-white/15">
    <div className="max-w-7xl mx-auto px-5 md:px-8">
      <div className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <BrandLogo variant="onDark" className="h-16 sm:h-[4.25rem] md:h-20 max-w-[min(100%,380px)] md:max-w-[460px]" />
          <p className="mt-4 text-sm max-w-md leading-relaxed text-slate-400">
            {COMPANY} — {PROFILE}
          </p>
          <p className="mt-4 font-display text-[var(--fe-orange)] font-semibold">{TAGLINE}</p>
        </div>

        <div className="md:col-span-3">
          <div className="fe-overline text-[var(--fe-orange)] mb-2">Explore</div>
          <ul className="space-y-2.5 text-sm">
            {NAV.map(({ href, label }) => (
              <li key={href}>
                <a href={href} className="text-slate-300 hover:text-white transition-colors">
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="fe-overline text-[var(--fe-orange)] mb-2">Contact</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[var(--fe-orange)] shrink-0" />
              <a href={MAILTO} className="hover:text-white break-all">
                {EMAIL}
              </a>
            </li>
            {PHONE ? (
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[var(--fe-orange)] shrink-0" />
                <a href={`tel:${PHONE}`} className="hover:text-white">
                  {PHONE_DISPLAY}
                </a>
              </li>
            ) : null}
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[var(--fe-orange)] shrink-0" />
              <span>{LOCATION}</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-8 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          © {new Date().getFullYear()} {COMPANY}. All rights reserved.
        </p>
        <p className="text-xs text-slate-500 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <a
            href={`${BASE}/admin/login.html`}
            className="text-slate-400 hover:text-[var(--fe-orange)] transition-colors"
            data-testid="footer-staff-login"
          >
            Staff login
          </a>
        </p>
      </div>
    </div>
  </footer>
);

const StickyCta = () => (
  <a
    href={WHATSAPP_URL || MAILTO}
    {...(WHATSAPP_URL ? { target: "_blank", rel: "noreferrer" } : {})}
    data-testid="sticky-cta"
    aria-label={WHATSAPP_URL ? "Chat on WhatsApp" : "Email us"}
    className={`fe-pulse fixed bottom-5 right-5 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-colors ${
      WHATSAPP_URL ? "bg-[#25D366] hover:bg-[#1da851] text-white" : "bg-[var(--fe-orange)] hover:bg-[var(--fe-orange-deep)] text-white"
    }`}
  >
    {WHATSAPP_URL ? (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
        <path d="M20.52 3.48A11.9 11.9 0 0 0 12.06 0C5.5 0 .18 5.33.18 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.33-1.67a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.89-5.33 11.89-11.9 0-3.18-1.24-6.17-3.44-8.41zM12.07 21.8h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.76.99 1-3.66-.24-.38a9.86 9.86 0 0 1-1.51-5.26c0-5.46 4.45-9.9 9.93-9.9 2.65 0 5.14 1.03 7.01 2.9a9.83 9.83 0 0 1 2.9 7.01c0 5.46-4.45 9.89-9.92 9.89zm5.44-7.41c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.14-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.58-.01c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.09 4.49.71.31 1.27.49 1.7.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.12-.27-.2-.57-.35z" />
      </svg>
    ) : (
      <Mail className="w-6 h-6" />
    )}
  </a>
);

function App() {
  return (
    <div className="fe-app fe-page-bg" data-testid="anisun-app">
      <Header />
      <main className="text-[var(--fe-ink)]">
        <Hero />
        <KeywordMarquee />
        <Services />
        <Bundle />
        <Industries />
        <Pricing />
        <About />
        <WhyUs />
        <Contact />
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}

export default App;
